import React from "react";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import dayjs from "dayjs";

import FlexBetween from './FlexBetween';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";

// Chart points carry `date` as "YYYY-MM-DD" (daily) or "YYYY-MM-DD HH:00" (hourly)
const formatPointDate = (raw) => {
  if (!raw) return "";
  const [day, time] = String(raw).split(" ");
  const d = dayjs(day);
  if (!d.isValid()) return String(raw);
  if (!time) return d.format("ddd D MMM YYYY");
  const [h = "0", m = "00"] = time.split(":");
  return `${d.format("ddd D MMM")}, ${String(h).padStart(2, "0")}:${m.padStart(2, "0")}`;
};

// Hover card: date on top, value + unit below
const ChartTooltip = ({ active, payload, unit, float }) => {
  const theme = useTheme();
  if (!active || !payload?.length) return null;

  const point = payload[0];
  const raw = Number(point.value) || 0;
  const value = float === 0 || float === undefined ? Math.round(raw) : raw.toFixed(3);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        whiteSpace: "nowrap",
        pointerEvents: "none",
        backgroundColor: alpha(theme.palette.background.alt, 0.97),
        border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : theme.palette.neutral[200]}`,
        boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
      }}
    >
      <Typography sx={{ fontSize: "0.7rem", color: theme.palette.text.secondary, lineHeight: 1.3 }}>
        {formatPointDate(point.payload?.date)}
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, lineHeight: 1.3, color: theme.palette.text.primary }}>
        <Box component="span" sx={{ color: theme.palette.secondary.main }}>{value}</Box>
        {unit ? ` ${unit}` : ""}
      </Typography>
    </Box>
  );
};

// Stat Box
// unit: what one chart point counts ("logins", "flights", …); shown in the hover card
const StateBox = ({ title, value, increase, icon, description, chartData, dataKey, float, unit }) => {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const gradientId = `statbox-${dataKey}`;

  return (
    <Box
      sx={{
        // xs/sm: 1 column wide. md/lg: 2 columns wide.
        gridColumn: { xs: "span 1", md: "span 2" },
        gridRow: "span 1",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "0.25rem",
        p: { xs: "1rem 0.875rem", md: "1.25rem 1rem" },
        minWidth: 0,          // allows the chart to shrink inside the grid
        position: "relative",
        backgroundColor: theme.palette.background.alt,
        borderRadius: "0.55rem",
        // the hover card may stick out above the box
        "&:hover": { zIndex: 2 },
      }}
    >
      <FlexBetween gap="0.5rem" alignItems="flex-start">
        {/* title */}
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.secondary[100],
            minWidth: 0,
            fontSize: { xs: "0.9rem", md: undefined },
          }}
        >
          {title}
        </Typography>

        {/* icon */}
        <Box sx={{ flexShrink: 0 }}>{icon}</Box>
      </FlexBetween>

      {/* value */}
      <Typography
        variant="h3"
        fontWeight="600"
        sx={{
          color: theme.palette.secondary[200],
          fontSize: { xs: "1.6rem", md: undefined },
        }}
      >
        {!value ? 0 : value}
      </Typography>

      <FlexBetween gap="0.5rem">
        {/* increase % */}
        <Typography
          variant="h5"
          fontStyle="italic"
          sx={{ color: theme.palette.light }}
        >
          {increase}
        </Typography>

        {/* description */}
        <Typography sx={{ fontSize: { xs: "0.6rem", md: "0.8rem" }, color: theme.palette.text.secondary, textAlign: "right" }}>
          {description}
        </Typography>
      </FlexBetween>

      <ResponsiveContainer width="100%" height={34}>
        <AreaChart data={!chartData ? [] : chartData} margin={{ top: 3, right: 2, bottom: 0, left: 2 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.55} />
              <stop offset="100%" stopColor={accent} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <Tooltip
            content={<ChartTooltip unit={unit} float={float} />}
            cursor={{ stroke: alpha(accent, 0.6), strokeWidth: 1, strokeDasharray: "3 3" }}
            allowEscapeViewBox={{ x: false, y: true }}
            position={{ y: -54 }}
            wrapperStyle={{ zIndex: 10, outline: "none" }}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={accent}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            activeDot={{ r: 3, fill: accent, stroke: theme.palette.background.alt, strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default StateBox;
